import React, { useState, useEffect, useMemo } from 'react';
import { timetableService } from '../../lib/services/timetable';
import TimetableGrid from './TimetableGrid';
import ElectiveSelector from './ElectiveSelector';
import SearchFilter from './SearchFilter';
import StatisticsPanel from './StatisticsPanel';
import TimetableManagement from './TimetableManagement';
import './TimetableModule.css';

const TimetableModule = ({ currentUser, onBack, onTabChange }) => {
  const [currentView, setCurrentView] = useState('classes');
  const [selectedItem, setSelectedItem] = useState(null);
  const [filteredClasses, setFilteredClasses] = useState([]);
  const [filteredTeachers, setFilteredTeachers] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [studentElectives, setStudentElectives] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({});
  const [timetableData, setTimetableData] = useState(null);
  const [timetableLoading, setTimetableLoading] = useState(false);
  const [organizedTimetableData, setOrganizedTimetableData] = useState(null);

  // Helper function to organize timetable data by period_sequence
  const organizeTimetableData = (rawData) => {
    const organizedData = {
      monday: new Array(15).fill(null),
      tuesday: new Array(15).fill(null),
      wednesday: new Array(15).fill(null),
      thursday: new Array(15).fill(null),
      friday: new Array(15).fill(null)
    };

    const dayMapping = {
      'Monday': 'monday',
      'Tuesday': 'tuesday',
      'Wednesday': 'wednesday',
      'Thursday': 'thursday',
      'Friday': 'friday'
    };

    if (rawData && Array.isArray(rawData)) {
      rawData.forEach(entry => {
        const dayKey = dayMapping[entry.day_of_week];
        if (dayKey && entry.period_sequence && entry.period_sequence >= 1 && entry.period_sequence <= 15) {
          // Place the entry at the correct index (period_sequence - 1)
          organizedData[dayKey][entry.period_sequence - 1] = {
            subject: entry.subject || '',
            class_id: entry.class_id || '',
            classroom: entry.classroom || '',
            notes: entry.notes || '',
            period_sequence: entry.period_sequence,
            period_node: entry.period_node,
            period_number: entry.period_number,
            time_slot: entry.time_slot,
            week_type: entry.week_type || 'regular',
            type: entry.subject_type || 'core',
            elective_group: entry.elective_group,
            id: entry.id || `${entry.day_of_week}_${entry.period_sequence}_${Date.now()}`
          };
        }
      });
    }

    return organizedData;
  };

  // Time slots and day names for 15-period structure
  const timeSlots = [
    '8:00-8:10',  // 班主任節
    '8:10-8:45',  // Period 1
    '8:45-9:20',  // Period 2
    '9:20-9:55',  // Period 3
    '9:55-10:15', // Recess
    '10:15-10:50', // Period 4
    '10:50-11:25', // Period 5
    '11:25-11:30', // Recess
    '11:30-12:05', // Period 6
    '12:05-12:40', // Period 7
    '12:40-1:50',  // Lunch
    '1:50-2:25',   // Period 8
    '2:25-3:00',   // Period 9
    '3:00-3:35',   // Period 10
    '3:35-3:50'    // 班主任節
  ];

  const dayNames = ['星期一', '星期二', '星期三', '星期四', '星期五'];
  const dayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [classes, teachers, students, statistics] = await Promise.all([
        timetableService.getClasses(),
        timetableService.getTeachers(),
        timetableService.getAllStudents(),
        timetableService.getTimetableStats()
      ]);

      setFilteredClasses(classes);
      setFilteredTeachers(teachers);
      setFilteredStudents(students);
      setStats(statistics);

      // Initialize student electives from database
      const electivesMap = {};
      students.forEach(student => {
        if (student.elective_subjects) {
          electivesMap[student.id] = student.elective_subjects;
        }
      });
      setStudentElectives(electivesMap);

    } catch (err) {
      console.error('Error loading initial data:', err);
      setError('Failed to load timetable data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleElectiveChange = async (studentId, electives) => {
    try {
      await timetableService.updateStudentElectives(studentId, electives);
      setStudentElectives(prev => ({
        ...prev,
        [studentId]: electives
      }));
    } catch (err) {
      console.error('Error updating student electives:', err);
      setError('Failed to update student electives. Please try again.');
    }
  };

  const renderItemList = (items, type) => {
    const getTypeIcon = (type) => {
      switch(type) {
        case 'class': return '🏛️';
        case 'teacher': return '👨‍🏫';
        case 'student': return '👨‍🎓';
        default: return '📋';
      }
    };

    const getTypeColor = (type) => {
      switch(type) {
        case 'class': return 'from-blue-500 to-blue-600';
        case 'teacher': return 'from-emerald-500 to-emerald-600';
        case 'student': return 'from-purple-500 to-purple-600';
        default: return 'from-gray-500 to-gray-600';
      }
    };

    // Special 4x6 grid layout for class management
    if (type === 'class') {
      return (
        <div className="classes-4x6-grid">
          <div className="grid-4-cols">
            {items.map(item => (
              <div
                key={item.id}
                onClick={() => setSelectedItem({ ...item, type })}
                className="item-card class-card-compact"
              >
                <div className="card-content">
                  <div className="card-header">
                    <div className={`card-icon-container ${type}`}>
                      <span className="card-icon">{getTypeIcon(type)}</span>
                    </div>
                    {item.class_level >= 6 && (
                      <span className="elective-badge">
                        選修制
                      </span>
                    )}
                  </div>
                  
                  <div className="card-title-section">
                    <h3 className="card-title">
                      {item.class_name}
                    </h3>
                    <p className="card-subtitle">班級編號: {item.class_id}</p>
                  </div>
                  
                  <div className="card-details">
                    <div className="card-detail-row">
                      <span className="card-detail-icon">📚</span>
                      <span className="card-detail-text">中{item.class_level}</span>
                    </div>
                    <div className="card-detail-row">
                      <span className="card-detail-icon">🏷️</span>
                      <span className="card-detail-text">{item.class_section}</span>
                    </div>
                  </div>
                  
                  <div className="card-footer">
                    <div className="card-footer-text">點擊查看時間表</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Special 4-column grid layout for teacher management
    if (type === 'teacher') {
      return (
        <div className="teachers-4col-grid">
          <div className="grid-4-cols">
            {items.map(item => (
              <div
                key={item.id}
                onClick={() => setSelectedItem({ ...item, type })}
                className="item-card teacher-card-compact"
              >
                <div className="card-content">
                  <div className="card-header">
                    <div className={`card-icon-container ${type}`}>
                      <span className="card-icon large">{getTypeIcon(type)}</span>
                    </div>
                  </div>
                  
                  <div className="card-title-section">
                    <h3 className="card-title">
                      {item.teacher_name}
                    </h3>
                  </div>
                  
                  <div className="card-details">
                    <div className="card-detail-row">
                      <span className="card-detail-icon emerald">📖</span>
                      <span className="card-detail-text">任教科目</span>
                    </div>
                    <div className="subjects-display">
                      {Array.isArray(item.subjects) ? item.subjects.join(', ') : item.subjects}
                    </div>
                  </div>
                  
                  <div className="card-footer">
                    <div className="card-footer-text">點擊查看時間表</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Special 6-column grid layout for student management
    if (type === 'student') {
      return (
        <div className="students-6col-grid">
          <div className="grid-6-cols">
            {items.map(item => (
              <div
                key={item.id}
                onClick={() => setSelectedItem({ ...item, type })}
                className="item-card student-card-compact"
              >
                <div className="card-content">
                  <div className="card-header">
                    <div className={`card-icon-container ${type}`}>
                      <span className="card-icon">{getTypeIcon(type)}</span>
                    </div>
                  </div>
                  
                  <div className="card-title-section">
                    <h3 className="card-title">
                      {item.student_name}
                    </h3>
                  </div>
                  
                  <div className="card-details">
                    <div className="card-detail-row compact">
                      <span className="card-detail-icon purple">🏫</span>
                      <span className="card-detail-text">{item.saas_classes?.class_name || 'N/A'}</span>
                    </div>
                    {item.elective_subjects && item.elective_subjects.length > 0 && (
                      <div className="elective-subjects-display">
                        <span>🎯</span>
                        <span className="card-detail-text">{item.elective_subjects.join(', ')}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="card-footer-compact">
                    <div className="card-footer-text-compact">點擊查看</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Fallback layout (should not be reached with current implementation)
    return (
      <div className="fallback-grid">
        {items.map(item => (
          <div
            key={item.id}
            onClick={() => setSelectedItem({ ...item, type })}
            className="fallback-card"
          >
            <div className="fallback-card-content">
              <div className="fallback-card-header">
                <div className="fallback-card-left">
                  <div className={`fallback-card-icon-container ${type}`}>
                    <span className="card-icon large">{getTypeIcon(type)}</span>
                  </div>
                  <div>
                    <h3 className="fallback-card-title">
                      {item.name || item.student_name || item.teacher_name}
                    </h3>
                  </div>
                </div>
              </div>
              
              <div className="fallback-card-footer">
                <span className="fallback-card-footer-text">點擊查看時間表</span>
                <span className="fallback-card-arrow">→</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const loadTimetableData = async () => {
    if (!selectedItem) {
      setTimetableData(null);
      return;
    }

    try {
      setTimetableLoading(true);
      console.log('Loading timetable data for:', selectedItem);
      
      let data;
      if (currentView === 'teachers') {
        // For teachers, use getTeacherTimetable
        const teacherId = selectedItem.teacher_id || selectedItem.id;
        console.log('Loading teacher timetable for teacher ID:', teacherId);
        console.log('Selected item:', selectedItem);
        data = await timetableService.getTeacherTimetable(teacherId);
      } else if (currentView === 'students') {
        // For students, we might need a different method or use class timetable
        console.log('Loading student timetable (using class timetable) for class:', selectedItem.class_id);
        data = await timetableService.getClassTimetable(selectedItem.class_id || selectedItem.id);
      } else {
        // For classes, use getClassTimetable
        console.log('Loading class timetable for class ID:', selectedItem.id);
        data = await timetableService.getClassTimetable(selectedItem.id);
      }
      
      console.log('Timetable data loaded:', data);
      setTimetableData(data);
    } catch (err) {
      console.error('Error loading timetable:', err);
      setTimetableData(null);
    } finally {
      setTimetableLoading(false);
    }
  };

  // Organize timetable data when it's loaded
  useEffect(() => {
    if (timetableData) {
      console.log('📊 Setting timetable data in TimetableModule:', timetableData);
      setOrganizedTimetableData(timetableData);
    }
  }, [timetableData]);

  // Load timetable data when selectedItem changes
  useEffect(() => {
    loadTimetableData();
  }, [selectedItem]);

  const renderTimetable = () => {
    if (!selectedItem) return null;

    if (timetableLoading) {
      return (
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-8 text-center">
          <div className="relative mx-auto mb-4 w-12 h-12">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200"></div>
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent absolute top-0"></div>
          </div>
          <p className="text-blue-800 font-medium">載入時間表中...</p>
          <p className="text-blue-600 text-sm mt-1">正在獲取最新資料</p>
        </div>
      );
    }

    if (!timetableData || Object.keys(timetableData).length === 0) {
      return (
        <div className="bg-gradient-to-br from-yellow-50 to-orange-50 border border-yellow-200 rounded-xl p-8 text-center">
          <div className="text-yellow-500 text-4xl mb-4">📅</div>
          <p className="text-yellow-800 font-medium">暫無時間表資料</p>
          <p className="text-yellow-600 text-sm mt-1">請聯繫管理員添加時間表</p>
        </div>
      );
    }

    console.log('Rendering TimetableGrid with data:', timetableData);
    console.log('Selected item:', selectedItem);
    
    // Generate appropriate title based on current view
    let title;
    if (currentView === 'teachers') {
      title = `${selectedItem.teacher_name || selectedItem.name} - 時間表`;
    } else if (currentView === 'students') {
      title = `${selectedItem.student_name || selectedItem.name} - 時間表`;
    } else {
      title = `${selectedItem.class_name || selectedItem.name} - 時間表`;
    }
    
    return (
      <div>
        {/* Add TimetableManagement component for teachers */}
        {currentView === 'teachers' && selectedItem && (
          <TimetableManagement
            teacherId={selectedItem.teacher_id || selectedItem.id}
            teacherName={selectedItem.teacher_name || selectedItem.name}
            timetableData={timetableData}
            onTimetableUpdated={() => {
              // Refresh timetable data when updated
              loadTimetableData();
            }}
          />
        )}
        
        <TimetableGrid
          data={organizedTimetableData || timetableData}
          title={title}
          hasElectives={selectedItem.class_level >= 6}
          timeSlots={timeSlots}
          dayNames={dayNames}
          dayKeys={dayKeys}
        />
      </div>
    );
  };

  const renderElectiveManagement = () => {
    const electiveStudents = filteredStudents.filter(student => 
      student.saas_classes?.class_level >= 6
    );

    if (electiveStudents.length === 0) return null;

    return (
      <div className="elective-management">
        <h3 className="elective-title">🎯 選修科目管理</h3>
        {electiveStudents.map(student => (
          <ElectiveSelector
            key={student.id}
            studentId={student.id}
            studentName={student.student_name}
            currentElectives={studentElectives[student.id] || student.elective_subjects || []}
            onElectiveChange={handleElectiveChange}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <div className="loading-spinner-container">
            <div className="loading-spinner-outer"></div>
            <div className="loading-spinner-inner"></div>
          </div>
          <p className="loading-text">載入時間表資料中...</p>
          <p className="loading-subtext">請稍候片刻</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-content">
          <div className="error-card">
            <div className="error-icon">⚠️</div>
            <h3 className="error-title">載入失敗</h3>
            <p className="error-message">{error}</p>
            <button
              onClick={loadInitialData}
              className="error-button"
            >
              重新載入
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-container">
      {/* Enhanced Header */}
      <div className="header-container">
        <div className="header-gradient"></div>
        <div className="header-content">
          <div className="header-main">
            <div className="header-left">
              <div className="header-icon">
                <span>🏫</span>
              </div>
              <div className="header-text">
                <h1>學校時間表管理系統</h1>
                <p>管理班級時間表、教師安排及選修科目</p>
              </div>
            </div>
            {onBack && (
              <button
                onClick={onBack}
                className="back-button"
              >
                <span>←</span>
                <span>返回主頁</span>
              </button>
            )}
          </div>
          
          {/* Enhanced Navigation */}
          <nav className="navigation">
            {[
              { key: 'classes', label: '班級管理', icon: '🏛️', colorFrom: '#3b82f6', colorTo: '#1d4ed8' },
              { key: 'teachers', label: '教師管理', icon: '👨‍🏫', colorFrom: '#10b981', colorTo: '#059669' },
              { key: 'students', label: '學生管理', icon: '👨‍🎓', colorFrom: '#8b5cf6', colorTo: '#7c3aed' }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => {
                  setCurrentView(tab.key);
                  setSelectedItem(null);
                }}
                className={`nav-button ${currentView === tab.key ? 'active' : 'inactive'}`}
                style={currentView === tab.key ? {
                  '--color-from': tab.colorFrom,
                  '--color-to': tab.colorTo
                } : {}}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      <div className="content-container">
        {selectedItem ? (
          <div>
            <div className="selected-item-container">
              <button
                onClick={() => setSelectedItem(null)}
                className="back-to-list-button"
              >
                <span>←</span>
                <span>返回列表</span>
              </button>
            </div>
            <div className="timetable-card">
              {renderTimetable()}
            </div>
          </div>
        ) : (
          <div>
            {currentView === 'classes' && (
              <>
                <StatisticsPanel data={filteredClasses} type="class" stats={stats} />
                <SearchFilter
                  data={filteredClasses}
                  onFilter={setFilteredClasses}
                  type="class"
                  placeholder="搜尋班級名稱..."
                />
                {renderItemList(filteredClasses, 'class')}
              </>
            )}

            {currentView === 'teachers' && (
              <>
                <StatisticsPanel data={filteredTeachers} type="teacher" stats={stats} />
                <SearchFilter
                  data={filteredTeachers}
                  onFilter={setFilteredTeachers}
                  type="teacher"
                  placeholder="搜尋教師姓名..."
                />
                {renderItemList(filteredTeachers, 'teacher')}
              </>
            )}

            {currentView === 'students' && (
              <>
                <StatisticsPanel data={filteredStudents} type="student" stats={stats} />
                <SearchFilter
                  data={filteredStudents}
                  onFilter={setFilteredStudents}
                  type="student"
                  placeholder="搜尋學生姓名..."
                />
                {renderElectiveManagement()}
                {renderItemList(filteredStudents, 'student')}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TimetableModule;
